import "./App.css";
import mainApi from "../../utils/api.js";
import { preloadScript, getCachedScript } from "../../utils/scriptCache.js";
import { INITIAL_TAGS } from "../../utils/constants.js";
import {
  saveCepStorage,
  getCepStorage,
  getItem,
  setItem,
  removeItem,
} from "../../utils/cepStorage.js";
import { useCepStorage } from "../../hooks/useCepStorage.jsx";
import { usePersistentState } from "../../hooks/usePersistentState.jsx";
import { useScriptVersion } from "../../hooks/useScriptVersion.jsx";
import CurrentUserContext from "../../contexts/CurrentUserContext.jsx";
import { useState, useEffect, memo } from "react";
import {
  Navigate,
  useRoutes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import useVisible from "../../hooks/useVisible.jsx";
import { CATEGORIES } from "../../utils/constants";
import {
  hasCategory,
  isSubscriptionActive,
} from "../../utils/subscription.js";

import Login from "../Login/Login.jsx";
import Register from "../Register/Register.jsx";
import InfoTooltip from "../InfoTooltip/InfoTooltip.jsx";
import SubscriptionCheck from "../SubscriptionCheck/SubscriptionCheck.jsx";
import DeviceCheck from "../DeviceCheck/DeviceCheck.jsx";
import Header from "../Header/Header.jsx";
import Navigation from "../Navigation/Navigation.jsx";
import Tags from "../Tags/Tags.jsx";
import ButtonList from "../ButtonList/ButtonList.jsx";
import Footer from "../Footer/Footer.jsx";
import LoadData from "../LoadData/LoadData.jsx";
import TelegramLink from "../TelegramLink/TelegramLink.jsx";
import EmailVerifier from "../EmailVerifier/EmailVerifier.jsx";
import Preloader from "../Preloader/Preloader.jsx";
import Snowfall from "../Snowfall/Snowfall.jsx";

// Выносим MainContent в отдельный компонент
const MainContent = memo(
  ({
    currentUser,
    handleUpdateUser,
    handleSignOut,
    scriptVersion,
    findButton,
    setFindButton,
    selectedCategory,
    setSelectedCategory,
    selectedTag,
    setSelectedTag,
    tags,
    setTags,
    buttons,
    categories,
    buttonViewMode,
    setButtonViewMode,
  }) => (
    <>
      <Header
        handleUpdateUser={handleUpdateUser}
        handleSignOut={handleSignOut}
        scriptVersion={scriptVersion}
        findButton={findButton}
        setFindButton={setFindButton}
        buttonViewMode={buttonViewMode}
        setButtonViewMode={setButtonViewMode}
      />
      <Navigation
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
        categories={categories}
        saveCepStorage={saveCepStorage}
        getCepStorage={getCepStorage}
        useCepStorage={useCepStorage}
      />
      <Tags
        currentTag={selectedTag}
        onTagChange={setSelectedTag}
        tags={tags}
        setTags={setTags}
      />
      <ButtonList
        currentUser={currentUser}
        saveCepStorage={saveCepStorage}
        getCepStorage={getCepStorage}
        useCepStorage={useCepStorage}
        useVisible={useVisible}
        buttons={buttons}
        search={findButton}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedTag={selectedTag}
        tags={tags}
        buttonViewMode={buttonViewMode}
      />
      <Footer />
    </>
  ),
);

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);

  const [selectedCategory, setSelectedCategory] = usePersistentState(
    "selectedCategory",
    CATEGORIES[0],
    {
      raw: true,
      validator: (value) => (CATEGORIES.includes(value) ? value : CATEGORIES[0]),
    },
  );

  const [selectedTag, setSelectedTag] = useVisible(null);
  const [tags, setTags] = useState([]);
  const [buttons, setButtons] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [findButton, setFindButton] = useState("");
  const [isLoader, setIsLoader] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [scriptVersion, setScriptVersion] = useScriptVersion("");
  const [load, setLoad] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [device, setDevice] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [isTelegramLinked, setIsTelegramLinked] = usePersistentState(
    "isTelegramLinked",
    false,
  );
  const [emailVerifier, setEmailVerifier] = usePersistentState(
    "emailVerifier",
    false,
  );
  const [buttonViewMode, setButtonViewMode] = useCepStorage(
    "buttonViewMode",
    "list",
  );
  const normalizedButtonViewMode =
    buttonViewMode === "grid" ? "grid" : "list";

  const [isInfoTooltip, setIsInfoTooltip] = useState({
    isOpen: false,
    status: true,
    text: "",
  });

  const closeInfoTooltip = () => {
    setIsInfoTooltip((prev) => ({ ...prev, isOpen: false }));
  };

  const handleRegister = ({
    name,
    family,
    login,
    email,
    password,
    telegram,
  }) => {
    setIsLoader(true);
    mainApi
      .createUser({
        name,
        family,
        login,
        email,
        password,
        telegram: { username: telegram },
      })
      .then((response) => {
        if (response.data.email === email) {
          handleLogin({ email, password });
        }
      })
      .catch((err) => {
        setServerError(err?.message || "An error occurred during registration");
      })
      .finally(() => setIsLoader(false));
  };

  const handleLogin = ({ email, password }) => {
    setIsLoader(true);
    mainApi
      .login({ email, password })
      .then((jwt) => {
        if (!jwt.token) throw new Error("JWT token is missing.");
        setItem("jwt", jwt.token);
        return mainApi.getUserInfo();
      })
      .then((data) => {
        if (data) {
          setLoggedIn(true);
          setCurrentUser(data);
          navigate("/", { replace: true });
        }
      })
      .catch((err) => {
        const errorMessage = err?.message || "An error occurred during login";
        setServerError(errorMessage);
        removeItem("jwt");
      })
      .finally(() => setIsLoader(false));
  };

  const handleUpdateUser = (id, data) => {
    mainApi
      .updateUser(id, data)
      .then(() => {
        console.log("Update user info:", id);
      })
      .catch((error) => {
        console.error("Failed to update user info:", id, error);
        setIsInfoTooltip({
          isOpen: true,
          status: false,
          text: "Failed to update user info: " + error,
        });
      });
  };

  const handleSignOut = () => {
    if (currentUser?._id) {
      mainApi
        .updateUser(currentUser._id, {
          license: {
            status: false,
            hostName: "",
            userName: "",
          },
        })
        .catch((error) => {
          console.error(
            "Failed to update subscription for user:",
            currentUser._id,
            error,
          );
        });
    }
    setCurrentUser({});
    setDevice(false);
    setLoggedIn(false);
    removeItem("jwt");
  };

  // Эффект для проверки production среды
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      setDataLoaded(true);
    }
  }, []);

  // Эффект для инициализации пользователя
  useEffect(() => {
    const jwt = getItem("jwt");
    if (!jwt) {
      setLoad(true);
      return;
    }

    setIsLoader(true);
    const code = getCachedScript();
    if (code && process.env.NODE_ENV === "production") {
      window.jsx.evalScript(
        `run(ae2DeleteExtensionData());`,
        function (result) {},
      );
    }
    mainApi
      .getUserInfo()
      .then((data) => {
        if (!data) return;

        setLoggedIn(true);
        setCurrentUser(data);

        if (!data.license?.status) return;

        if (code && process.env.NODE_ENV === "production") {
          window.jsx.evalScript(`run(userInfo());`, function (result) {
            if (!result || result === "undefined") return;

            const userInfo = JSON.parse(result);
            if (!device) {
              setDevice({
                hostName: userInfo.hostName,
                userName: userInfo.userName,
              });
            }

            if (userInfo.hostName === data.license.hostName) {
              const deviceUpdate = {
                license: {
                  status: true,
                  hostName: userInfo.hostName,
                  userName: userInfo.userName,
                },
              };

              mainApi
                .updateUser(data._id, deviceUpdate)
                .then(setCurrentUser)
                .catch(console.error);
            }
          });
        }
      })
      .catch((err) =>
        setIsInfoTooltip({
          isOpen: true,
          status: false,
          text: err?.message || "An error occurred while fetching data",
        }),
      )
      .finally(() => {
        setIsLoader(false);
        setLoad(true);
        setLoading(false);
      });
  }, []);

  // Эффект для обновления пользовательских данных
  useEffect(() => {
    if (!currentUser) return;

    const { emailVerifier: emailVerifierData, telegram } = currentUser;

    if (emailVerifierData?.verify) {
      const emailVerify = emailVerifierData.verify;
      setEmailVerifier(emailVerify);
    }

    if (telegram?.isLinked) {
      setIsTelegramLinked(telegram.isLinked);
    }

    const savedTags = getCepStorage("tags");
    if (savedTags && savedTags.length > 0) {
      setTags(savedTags);
    } else {
      saveCepStorage("tags", INITIAL_TAGS);
      setTags(INITIAL_TAGS);
    }
  }, [currentUser]);

  // Эффект для загрузки кнопок
  useEffect(() => {
    if (loggedIn) {
      setIsLoader(true);
      mainApi
        .getButtons()
        .then((fetchedButtons) => {
          setButtons(
            fetchedButtons.sort((a, b) => a.name.localeCompare(b.name)),
          );
        })
        .catch((err) => {
          setIsInfoTooltip({ isOpen: true, status: false, text: err });
        })
        .finally(() => {
          setIsLoader(false);
        });
      preloadScript();
    }
  }, [loggedIn]);

  useEffect(() => {
    if (loggedIn && currentUser) {
      const availableCategories = CATEGORIES.filter((category) => {
        const hasSubscriptionAccess =
          !currentUser.subscription || hasCategory(currentUser.subscription, category);

        const hasFreeOrUserButtons = buttons.some(
          (button) => button.category === category && button.access === "free",
        );

        return hasSubscriptionAccess || hasFreeOrUserButtons;
      });

      setFilteredCategories(availableCategories);
    }
  }, [buttons, loggedIn]);

  // Определяем роуты на верхнем уровне компонента
  const routes = [
    {
      path: "signup",
      element: loggedIn ? (
        <Navigate to="/" replace />
      ) : (
        <Register handleRegister={handleRegister} serverError={serverError} />
      ),
    },
    {
      path: "signin",
      element: loggedIn ? (
        <Navigate to="/" replace />
      ) : (
        <Login handleLogin={handleLogin} serverError={serverError} />
      ),
    },
    {
      path: "/",
      element: !loggedIn ? (
        <Navigate to="/signin" />
      ) : !emailVerifier ? (
        <EmailVerifier
          setEmailVerifier={setEmailVerifier}
          handleUpdateUser={handleUpdateUser}
          handleSignOut={handleSignOut}
        />
      ) : !isTelegramLinked ? (
        <TelegramLink
          setIsTelegramLinked={setIsTelegramLinked}
          handleUpdateUser={handleUpdateUser}
          handleSignOut={handleSignOut}
        />
      ) : !isSubscriptionActive(currentUser?.subscription) ? (
        <SubscriptionCheck handleSignOut={handleSignOut} />
      ) : !dataLoaded ? (
        <LoadData
          isOpen={!dataLoaded}
          onDataLoaded={() => setDataLoaded(true)}
          scriptVersion={scriptVersion}
          setScriptVersion={setScriptVersion}
        />
      ) : !(
          currentUser?.license?.hostName === device?.hostName ||
          currentUser?.role === "admin" ||
          currentUser?.login === "LordVaran"
        ) ? (
        <DeviceCheck
          device={device}
          setCurrentUser={setCurrentUser}
          setDevice={setDevice}
          handleSignOut={handleSignOut}
        />
      ) : (
        <MainContent
          currentUser={currentUser}
          handleUpdateUser={handleUpdateUser}
          handleSignOut={handleSignOut}
          scriptVersion={scriptVersion}
          findButton={findButton}
          setFindButton={setFindButton}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedTag={selectedTag}
          setSelectedTag={setSelectedTag}
          tags={tags}
          setTags={setTags}
          buttons={buttons}
          categories={filteredCategories}
          buttonViewMode={normalizedButtonViewMode}
          setButtonViewMode={setButtonViewMode}
        />
      ),
    },
  ];

  // Используем роутер безусловно
  const router = useRoutes(routes);

  if (!load) {
    return <Preloader isOpen={true} />;
  }

  return (
    <CurrentUserContext.Provider value={currentUser}>
      <div>
        {/* <Snowfall /> */}
        <InfoTooltip onClose={closeInfoTooltip} isInfoTooltip={isInfoTooltip} />
        {router}
        {isLoader && <Preloader isOpen={true} />}
      </div>
    </CurrentUserContext.Provider>
  );
}
